import React, { useCallback, useMemo, useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ChevronRight, ChevronLeft, PlusLg } from 'react-bootstrap-icons';
import { useContextEngine } from '../../contexts/ContextEngineContext';
import { useProjects } from '../../contexts/ProjectContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useUsage } from '../../contexts/UsageContext';
import { PLAN_LIMITS } from '../../types/usage.types';
import { ContextSource, SignalCategory } from '../../types/context.types';
import SignalChip, { getSourceLabel } from './SignalChip';
import SourceItem from './SourceItem';
import ContextReadinessMeter from './ContextReadinessMeter';
import LiveSuggestions from './LiveSuggestions';
import AddSourceModal from './AddSourceModal';
import ManualTagInput from './ManualTagInput';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import './SignalsSidebar.css';

const SignalsSidebar: React.FC = () => {
  const { isRightSidebarCollapsed, toggleRightSidebar } = useSidebar();
  const { currentProject } = useProjects();
  const { usageStats } = useUsage();
  const {
    signals,
    sources,
    deleteSource,
    loadingSources,
    readinessScore,
    readinessBreakdown,
    liveSuggestions,
    createManualSignal,
    toggleSignal,
    deleteSignal,
    onSignalClick,
  } = useContextEngine();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState<ContextSource | null>(null);
  const [showAddSourceModal, setShowAddSourceModal] = useState(false);

  const planLimits = PLAN_LIMITS[usageStats?.plan ?? 'starter'];
  const sourceLimit = planLimits.sourcesPerProject;
  const maxFileSize = planLimits.maxFileSize;
  const sourceCount = sources.length;
  const hasUnlimitedSources = sourceLimit === null;
  const remainingSlots = hasUnlimitedSources
    ? Number.POSITIVE_INFINITY
    : Math.max(0, sourceLimit - sourceCount);
  const atSourceLimit = !hasUnlimitedSources && sourceCount >= sourceLimit;
  const nearSourceLimit =
    !hasUnlimitedSources && sourceLimit > 0 && sourceCount / sourceLimit >= 0.8;

  const sourceCountClass = atSourceLimit
    ? 'source-count-at-limit'
    : nearSourceLimit
      ? 'source-count-near-limit'
      : '';

  const sourceMap = useMemo(() => new Map(sources.map((s) => [s.id, s])), [sources]);

  const activeCount = signals.filter((s) => s.isActive).length;

  const handleManualTagAdd = async (label: string, category: SignalCategory) => {
    if (!currentProject?.id) return;
    try {
      await createManualSignal(currentProject.id, label, category);
    } catch (err) {
      console.error('Error adding signal:', err);
    }
  };

  const handleAddAsSignal = async (text: string, category: SignalCategory) => {
    if (!currentProject?.id) return;
    try {
      await createManualSignal(currentProject.id, text.substring(0, 50), category);
    } catch (err) {
      console.error('Error adding suggestion as signal:', err);
    }
  };

  const handleSourceDeleteClick = (source: ContextSource) => {
    setSourceToDelete(source);
    setShowDeleteModal(true);
  };

  const handleConfirmSourceDelete = async () => {
    if (sourceToDelete) {
      try {
        await deleteSource(sourceToDelete.id);
      } catch (err) {
        console.error('Error deleting source:', err);
      }
      setShowDeleteModal(false);
      setSourceToDelete(null);
    }
  };

  const handleCancelSourceDelete = () => {
    setShowDeleteModal(false);
    setSourceToDelete(null);
  };

  const handleSourceBreadcrumbClick = useCallback((sourceId: string) => {
    const el = document.querySelector<HTMLElement>(`.source-item[data-source-id="${sourceId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      el.classList.add('source-item--highlight');
      setTimeout(() => el.classList.remove('source-item--highlight'), 1500);
    }
  }, []);

  if (isRightSidebarCollapsed) {
    return (
      <div className="signals-sidebar collapsed">
        <button
          className="sidebar-toggle-btn"
          onClick={toggleRightSidebar}
          title="Expand signals sidebar"
          aria-label="Expand signals sidebar"
        >
          <ChevronLeft size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="signals-sidebar">
      <div className="sidebar-header">
        <span className="sidebar-title">Context</span>
        <button
          className="sidebar-toggle-btn"
          onClick={toggleRightSidebar}
          title="Collapse signals sidebar"
          aria-label="Collapse signals sidebar"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="sidebar-content">
        <ContextReadinessMeter
          score={readinessScore}
          breakdown={readinessBreakdown}
          compact
        />

        <div className="sidebar-section">
          <h6 className="section-heading">
            Sources{' '}
            <span className={`source-count ${sourceCountClass}`}>
              ({sourceCount}
              {hasUnlimitedSources ? '' : ` / ${sourceLimit}`})
            </span>
          </h6>
          <div className="sources-list">
            {loadingSources && sources.length === 0 && (
              <p className="empty-text">Loading...</p>
            )}
            {!loadingSources && sources.length === 0 && (
              <p className="empty-text">No source files uploaded yet.</p>
            )}
            {sources.map((source) => (
              <SourceItem
                key={source.id}
                source={source}
                onDelete={handleSourceDeleteClick}
              />
            ))}
          </div>
          {atSourceLimit ? (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Source limit reached for your plan.</Tooltip>}
            >
              <span className="d-inline-block">
                <Button
                  variant="link"
                  size="sm"
                  className="add-source-btn p-0 mt-1"
                  disabled
                  style={{ pointerEvents: 'none' }}
                >
                  <PlusLg size={12} className="me-1" />
                  Add Source
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              variant="link"
              size="sm"
              className="add-source-btn p-0 mt-1"
              onClick={() => setShowAddSourceModal(true)}
              disabled={!currentProject?.id}
            >
              <PlusLg size={12} className="me-1" />
              Add Source
            </Button>
          )}
        </div>

        <hr className="sidebar-divider" />

        <div className="sidebar-section">
          <h6 className="section-heading">
            Signals ({activeCount}/{signals.length})
          </h6>
          <div className="signals-list">
            {signals.length === 0 && (
              <p className="empty-text">No signals yet. Upload a document or add tags.</p>
            )}
            {signals.map((signal) => (
              <SignalChip
                key={signal.id}
                signal={signal}
                sourceLabel={getSourceLabel(signal, sourceMap)}
                draggable
                onClick={onSignalClick ?? undefined}
                onToggle={(s) => toggleSignal(s.id)}
                onDelete={(s) => deleteSignal(s.id)}
                compact
              />
            ))}
          </div>

          {currentProject?.id && (
            <div className="add-signal-tag-input mt-2">
              <h6 className="add-signal-tag-input-title">Add Signal</h6>
              <ManualTagInput
                tags={[]}
                onTagAdd={handleManualTagAdd}
                onTagRemove={() => {}}
                maxTags={Number.MAX_SAFE_INTEGER}
                compact
              />
            </div>
          )}
        </div>

        <hr className="sidebar-divider" />

        <div className="sidebar-section">
          <h6 className="section-heading">Live Suggestions</h6>
          <LiveSuggestions
            suggestions={liveSuggestions}
            readinessScore={readinessScore}
            onAddAsSignal={handleAddAsSignal}
            onSourceClick={handleSourceBreadcrumbClick}
          />
        </div>
      </div>

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCancelSourceDelete}
        onConfirm={handleConfirmSourceDelete}
        itemType="File"
        itemName={sourceToDelete?.fileName}
      />

      <AddSourceModal
        show={showAddSourceModal}
        onHide={() => setShowAddSourceModal(false)}
        remainingSlots={hasUnlimitedSources ? Number.MAX_SAFE_INTEGER : remainingSlots}
        maxFileSize={maxFileSize}
        currentCount={sourceCount}
        limit={sourceLimit}
      />
    </div>
  );
};

export default SignalsSidebar;
